RivalIQ.apps.Analyze.module('widgets', function (widgets, App, Backbone, Marionette, $, _, d3) {

    'use strict';

    const Glance = widgets.Glance = Marionette.LayoutView.mixin({
        regions: {
            sparkline: '.x-sparkline-region'
        },

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        behaviors: {
            Information: {
                behaviorClass: RivalIQ.behaviors.Information
            }
        },

        triggers: function () {

            const me = this;
            // NOTE in order to be selectable the glance must not have a
            // sparkline URL defined (they interfere with one another, as the
            // click trigger handler stops event propagation halting sparkline
            // navigation)
            return (!me.getSparklineUrl() ? {
                click: 'selected'
            } : {});
        },

        tagName: function () {

            const me = this;
            return (me.hasUrl() && !me.isSelected() ? 'a' : 'div');
        },

        attributes: function () {

            const me = this;
            const attributes = {};

            if (me.hasUrl() && !me.isSelected()) {

                const helpers = _.result(me, 'templateHelpers');
                const data = _.extend(me.model.toJSON(), helpers);
                _.extend(attributes, data.allAttrs());
            }

            return attributes;
        },

        className: function () {

            const me = this;
            const size = _.defaults(me.model.get('size') || {}, {
                xs: 12,
                sm: 3
            });
            return _.reduce(size, function (memo, value, key) {

                return memo.concat(['col', key, value].join('-'));
            }, _.compact(['x-widget-analyze-glance', (me.isSelected() ? 'selected' : null)])).join(' ');
        },

        template: '#widgetAnalyzeGlanceTemplate',

        templateHelpers: function () {

            const me = this;

            return _.extend({
                insight: function () {

                    const data = this;
                    const description = _.result(data, 'description');
                    let fn = function () { return description; };

                    if (_.isObject(description)) {

                        fn = widgets.insights.getFromConfig(description, me);
                    }

                    return fn(me.getInsightData());
                },
                sparklineUrl: _.bind(me.getSparklineUrl, me)
            }, RivalIQ.apps.Shell.mixins.Navigable.templateHelpers());
        },

        onShow: function () {

            const me = this;
            me.showSparkline();
            me.$('[data-toggle="popover"]').popover();
        },

        showSparkline: function () {

            const me = this;
            let view = null;
            const model = me.model;
            const data = model.get('dataValues');

            if (data && !model.get('hideSparkline')) {
                view = new RivalIQ.graph.Spark({
                    data: data
                });
                me.sparkline.show(view);
            }
        },

        setSelected: function (isSelected) {
            const me = this;
            me.options.selected = isSelected;
            me.$el.toggleClass('selected', Boolean(isSelected));
        },

        rerender: function () {
            const me = this;
            // make sure no info popups get left around
            me.triggerMethod('info:destroy');
            me.render();
            me.onShow();
        },

        isSelected: function () {

            const me = this;
            return (me.getOption('selected') === true);
        },

        getInsightData: function () {

            const me = this;
            return me.model.get('dataValues');
        },

        getSparklineUrl: _.noop,

        getSparklineGraphParams: function () {
            const me = this;

            return me.model.get('sparklineParams') || {
                focusWidgetId: RivalIQ.enums.widgetTypes.METRIC_TIME_CHART
            };
        },

        hasUrl: function () {

            const me = this;
            const navigation = me.model.get('navigation');
            return (Boolean(_.get(navigation, 'url')));
        }
    });

    /**
     * Gets a metric from configuration.
     * @param config
     * @param presencePeriodSummary
     * @param companyId
     */
    function getMetricFromConfig(config, presencePeriodSummary, companyId) {

        const options = _.result(config, 'metric');

        const metricDefinition = new RivalIQ.models.MetricDefinition(_.clone(options));
        _.defaults(metricDefinition.attributes, {
            companyId: companyId,
            timePeriod: presencePeriodSummary.timePeriod,
            timeAnalysisMode: RivalIQ.enums.timeAnalysisMode.DAILY_VALUES
        });

        const metric = metricDefinition.fetch(presencePeriodSummary);

        const company = presencePeriodSummary.get(companyId).toJSON();

        const valueType = (_.result(options, 'valueType') || RivalIQ.enums.valueType.CURRENT);

        const shouldIncludeSubmetrics = (valueType === RivalIQ.enums.valueType.CURRENT);
        const metricData = presencePeriodSummary.compareData.getValueData(metric.id, valueType, _.defaults({
            shouldIncludeSubmetrics: shouldIncludeSubmetrics
        }, _.pick(metricDefinition.attributes, 'presenceId')));
        const companyMetricData = _.find(metricData, {
            companyId: companyId
        });

        return _.extend(metric, {
            company: company,
            dataValues: {
                company: companyMetricData,
                landscape: metricData
            }
        });
    }

    const MetricGlance = Glance.extend({
        initialize: function () {

            const me = this;
            const metric = me.getMetric();

            let daysOfData = null;
            let sparklineData = null;
            const days = metric.company.periodSummary.dates.mainPeriodDuration;

            if (_.isFinite(metric.dataValues.company.value)) {

                sparklineData = metric.getTimeSpanData();

                daysOfData = _.without(sparklineData, null).length;

                // NOTE we will not show the sparkline if we are looking at
                // daily values, and have fewer than 20% days in the period
                // with measurements (where a measurement is defined as not null)
                if (metric.timeAnalysisMode !== RivalIQ.enums.timeAnalysisMode.DAILY_VALUES || (daysOfData / days) >= 0.2) {

                    me.model.set({
                        dataValues: sparklineData
                    });
                }
            }
        },

        getMetric: function () {

            const me = this;
            const companyId = me.getProp('companyId');
            const presencePeriodSummary = me.getOption('presencePeriodSummary');
            return getMetricFromConfig(me.model.toJSON(), presencePeriodSummary, companyId);
        },

        getSparklineGraphParams: function (metric) {

            const me = this;

            metric = (metric || me.getMetric());

            const attributes = {
                metric: metric.id,
                timeAnalysisMode: metric.get('timeAnalysisMode')
            };

            const configuredSparklineParams = Glance.prototype.getSparklineGraphParams.call(me);

            return _.defaults(attributes, configuredSparklineParams);
        },

        serializeData: function () {

            const me = this;
            const metric = me.getMetric();

            return _.defaults(me.model.toJSON(), {
                description: me.getDescription(),
                title: metric.getTitle({
                    abbreviate: true
                }),
                value: metric.getValue({
                    format: true,
                    alternate: true
                }),
                unabbreviatedValue: metric.format(metric.getValue({
                    format: false
                }), {
                    expand: true
                })
            });
        },

        getDescription: function () {

            const me = this;
            const metric = me.getMetric();
            const insight = me.model.get('insight');

            return {
                presencePeriodSummary: metric.data,
                type: (insight || widgets.insights.type.RANK_OR_CHANGE)
            };
        },

        getInsightData: function () {

            const me = this;
            return me.getMetric();
        }
    });

    const ComputedGlance = Glance.extend({
        serializeData: function () {

            const me = this;
            const data = me.model.toJSON();

            const operands = _.result(data, 'operands');
            let operator = _.result(data, 'operator');

            if (_.isString(operator)) {

                operator = me.getOperatorFromType(operator);
            }

            const value = _.reduce(operands, function (memo, operand, index) {

                return (index === 0 ? operand : operator(memo, operand));
            });

            data.value = (_.isFinite(value) ? me.formatValue(value) : value);

            let unabbreviatedValue = '--';
            if (_.isFinite(value)) {
                if (value > 99) {
                    unabbreviatedValue = d3.format(',')(value);
                } else {
                    unabbreviatedValue = data.value;
                }
            }
            data.unabbreviatedValue = unabbreviatedValue;

            return data;
        },
        getOperatorFromType: function (type) {

            let operator = _.identity;

            if (type === 'division') {

                operator = function (a, b) {

                    return a / b;
                };
            }

            return operator;
        },
        formatValue: function (v) {

            return (v > 99 ?
                RivalIQ.formatter.formatAbbreviated(v) :
                RivalIQ.formatter.formats.fixed(v));
        }
    });

    const MetricPerDiemGlance = ComputedGlance.extend({
        initialize: function () {

            const me = this;
            const metric = MetricGlance.prototype.getMetric.call(me);
            const days = metric.company.periodSummary.dates.mainPeriodDuration;

            const value = metric.dataValues.company.value;
            const operands = [(!_.isFinite(value) ? '--' : value)];
            let dailyValues = null;

            if (_.isFinite(value)) {

                operands.push(days);
                dailyValues = metric.getTimeSpanData();
            }

            const data = me.model.toJSON();
            const subject = _.result(data, 'subject');
            const description = _.result(data, 'description');

            const attributes = _.extend({
                dataValues: dailyValues,
                operands: operands,
                operator: 'division',
                title: [subject, 'Per Day'].join(' '),
                description: (description || me.getDescription(subject))
            }, _.omit(data, 'metric', 'subject'));

            me.model.set(attributes);
        },

        getMetric: MetricGlance.prototype.getMetric,
        getSparklineGraphParams: MetricGlance.prototype.getSparklineGraphParams,
        getInsightData: MetricGlance.prototype.getInsightData,

        getDescription: function (subject) {

            return {
                subject: subject,
                type: widgets.insights.type.BEST_CHANNEL_PER_DIEM
            };
        }
    });

    const MetricPerMetricGlance = ComputedGlance.extend({
        initialize: function () {

            const me = this;
            const divisor = me.getMetric('divisor');
            const quotient = me.getMetric('quotient');

            let operands = [];
            let dailyValues = null;
            const divisorValue = divisor.dataValues.company.value;
            const quotientValue = quotient.dataValues.company.value;

            if (!_.isFinite(divisorValue) || !_.isFinite(quotientValue)) {

                operands = ['--'];
            } else if (divisorValue === 0) {

                // NOTE if the divisor is zero then we should force the values
                // so that we do not divide by zero
                operands = [0, 1];
            } else {

                operands = [quotientValue, divisorValue];
                dailyValues = quotient.getTimeSpanData();
            }

            const attributes = _.extend({
                dataValues: dailyValues,
                operands: operands,
                operator: 'division'
            }, _.omit(me.model.toJSON(), 'quotient', 'divisor'));

            me.model.set(attributes);
        },

        getMetric: function (property) {

            const me = this;
            const id = me.model.get(property);
            const companyId = me.getProp('companyId');
            const presencePeriodSummary = me.getOption('presencePeriodSummary');
            return getMetricFromConfig({
                metric: id
            }, presencePeriodSummary, companyId);
        },

        getSparklineGraphParams: function () {

            const me = this;
            const metric = me.getMetric('quotient');
            return MetricGlance.prototype.getSparklineGraphParams.call(me, metric);
        },

        getInsightData: function () {

            const me = this;
            const metric = me.getMetric('quotient');
            return metric.dataValues.company;
        }
    });

    /**
     * An enum for glance types.
     */
    Glance.type = {
        METRIC: MetricGlance,
        METRIC_PER_DIEM: MetricPerDiemGlance,
        METRIC_PER_METRIC: MetricPerMetricGlance
    };

}, window.d3);
